/**
 * The shell: who is signed in, which workspace, and which screen.
 *
 * The boot sequence is three questions in order, and each unhappy answer is a
 * *screen* rather than an error — which is the whole difference between a product
 * and an admin tool:
 *
 * 1. Is Clerk configured? If not, say so plainly; a stack trace teaches nobody.
 * 2. Is anybody signed in? If not, Clerk's own form, in our colours.
 * 3. Is there a workspace? If not, one question: what is your business called.
 *
 * Only after all three does the application proper exist, and by then every screen
 * can assume a workspace and a plan without checking.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError, helpUrl, siteUrl, type WorkspaceView } from "./api.js";
import { activateOrganisation, arriveFromInvitation, clerk, isConfigured, loadClerk, NotConfigured, signedIn } from "./clerk.js";
import { go, matchRoute, query, usePath } from "./router.js";
import { Button, Empty, Icon, Note, Toasts, toast } from "./ui/index.js";
import { History } from "./screens/History.js";
import { ImportDocument } from "./screens/ImportDocument.js";
import { SignIn } from "./screens/SignIn.js";
import { Welcome } from "./screens/Welcome.js";
import { Home } from "./screens/Home.js";
import { DataList } from "./screens/DataList.js";
import { DataImport } from "./screens/DataImport.js";
import { DataReview } from "./screens/DataReview.js";
import { DataEdit } from "./screens/DataEdit.js";
import { Designs } from "./screens/Designs.js";
import { Design } from "./screens/Design.js";
import { Matching } from "./screens/Matching.js";
import { FillIn } from "./screens/FillIn.js";
import { Documents } from "./screens/Documents.js";
import { Brand } from "./screens/Brand.js";
import { Sidebar } from "./shell/Sidebar.js";
import { Account, type WantedPlan } from "./screens/Account.js";
import { People } from "./screens/People.js";
import { Workspace, type WorkspaceContext } from "./workspace.js";

type Boot = { state: "loading" } | { state: "unconfigured" } | { state: "signed-out" } | { state: "no-workspace"; message: string; hint?: string } | { state: "ready"; view: WorkspaceView } | { state: "broken"; message: string; hint?: string };

export function App() {
  const [boot, setBoot] = useState<Boot>({ state: "loading" });
  const path = usePath();

  const loadWorkspace = useCallback(async () => {
    try {
      const view = await api.workspace();
      setBoot({ state: "ready", view });
    } catch (e) {
      const error = e instanceof ApiError ? e : null;
      if (error?.code === "no-workspace") setBoot({ state: "no-workspace", message: error.message, ...(error.hint ? { hint: error.hint } : {}) });
      else if (error?.code === "not-signed-in") setBoot({ state: "signed-out" });
      else setBoot({ state: "broken", message: error?.message ?? "BroadPaper could not start.", ...(error?.hint ? { hint: error.hint } : {}) });
    }
  }, []);

  /** Signed in: select the workspace an invitation was for, if this is the arrival from one, then open. */
  const enter = useCallback(async () => {
    await arriveFromInvitation();
    await loadWorkspace();
  }, [loadWorkspace]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isConfigured()) return setBoot({ state: "unconfigured" });
      try {
        const instance = await loadClerk();
        if (cancelled) return;
        // Clerk tells us when somebody signs in or out in another tab, and the
        // application has to follow: a signed-out tab left open is a tab whose
        // every click would fail.
        instance.addListener(() => {
          if (!signedIn()) setBoot({ state: "signed-out" });
        });
        if (!signedIn()) return setBoot({ state: "signed-out" });
        await enter();
      } catch (e) {
        if (cancelled) return;
        if (e instanceof NotConfigured) return setBoot({ state: "unconfigured" });
        setBoot({ state: "broken", message: "BroadPaper couldn't start.", hint: (e as Error).message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enter]);

  const context: WorkspaceContext | null = useMemo(
    () => (boot.state === "ready" ? { view: boot.view, refresh: loadWorkspace } : null),
    [boot, loadWorkspace]
  );

  /*
   * "Choose Pro" on the pricing page arrives as `?plan=pro`, on whatever address it
   * was sent to.
   *
   * Nothing is done with it until there is a workspace: the sign-in form sends
   * somebody back to this same address with its query intact, and Welcome leaves it
   * alone while a business is named, so the parameter is still here once the
   * workspace loads. Then it goes to the plan screen, which decides whether this person
   * can buy it.
   */
  useEffect(() => {
    if (boot.state !== "ready" || matchRoute(path, "/account")) return;
    const wanted = wantedPlan(path);
    if (wanted) go(`/account?${new URLSearchParams({ plan: wanted.plan, ...(wanted.cadence ? { cadence: wanted.cadence } : {}) }).toString()}`, { replace: true });
  }, [boot.state, path]);

  /*
   * A free document page on the site arrives as `?starter=invoice`: somebody made one
   * invoice there and wants to make all of them from a spreadsheet. Like `?plan`, it
   * survives sign-in and Welcome in the address, and once there is a workspace it goes
   * to Designs, which opens that starter as though its tile had been clicked.
   */
  useEffect(() => {
    if (boot.state !== "ready" || matchRoute(path, "/designs")) return;
    const starter = query(path).get("starter");
    if (starter && /^[a-z0-9-]{1,40}$/.test(starter)) go(`/designs?starter=${starter}`, { replace: true });
  }, [boot.state, path]);

  if (boot.state === "loading") {
    return (
      <div className="empty">
        <span className="spinner spinner--lg" />
        <p className="muted">Opening BroadPaper…</p>
      </div>
    );
  }

  if (boot.state === "unconfigured") {
    return (
      <div className="main">
        <div className="wrap--narrow stack" style={{ gap: 16 }}>
          <h1>Accounts are not configured here</h1>
          <Note tone="ask" title="VITE_CLERK_PUBLISHABLE_KEY is not set">
            This build of BroadPaper Cloud has no identity provider, so nobody can sign in. Set the key and rebuild — it is public by design and
            authorises nothing on its own.
          </Note>
        </div>
      </div>
    );
  }

  if (boot.state === "signed-out") return <SignIn onSignedIn={() => void enter()} />;

  if (boot.state === "no-workspace") {
    return (
      <Welcome
        message={boot.message}
        hint={boot.hint}
        onChosen={async (organisationId) => {
          // The token in the browser's hand was minted before the organisation
          // existed, or before it was chosen, so the next call would arrive with no
          // workspace. Asking Clerk to make it active reissues the token with it in.
          await activateOrganisation(organisationId);
          await loadWorkspace();
        }}
      />
    );
  }

  if (boot.state === "broken") {
    return (
      <div className="main">
        <div className="wrap--narrow stack" style={{ gap: 16 }}>
          <h1>BroadPaper couldn't start</h1>
          <Note tone="bad" title={boot.message}>
            {boot.hint ?? "Try reloading. If it keeps happening, email support@broadpaper.com and we will look straight away."}
          </Note>
          <div>
            <Button variant="primary" onClick={() => window.location.reload()}>
              Reload
            </Button>
          </div>
        </div>
      </div>
    );
  }

  /*
   * The designer takes the viewport, and nothing frames it.
   *
   * It already has two panels of its own and changes layout below 820px; a third
   * panel outside it leaves the canvas too narrow to judge a page on, which is the
   * one thing a page designer cannot spare. So a design is full screen with a way
   * back, and every other screen is a pane beside the tree.
   */
  // `/designs/import` has the shape of a design's address and is a screen in the
  // shell, so it is ruled out here rather than opened as a design called "import".
  const design = matchRoute(path, "/designs/:id");
  if (design && design.id !== "import") {
    return (
      <Workspace.Provider value={context!}>
        <div className="app">
          <Design id={design.id!} />
          <Toasts />
        </div>
      </Workspace.Provider>
    );
  }

  return (
    <Workspace.Provider value={context!}>
      <div className="app shell">
        <TopBar />
        <div className="shell__body">
          <Sidebar />
          <main className="shell__pane">
            <Screen path={path} />
          </main>
        </div>
        <Toasts />
      </div>
    </Workspace.Provider>
  );
}

/* ------------------------------------------------------------------ the bar */

/**
 * The bar: who you are, and nothing else.
 *
 * It held the four sections until the sidebar did. Two controls that both answer
 * "where am I" is how they drift out of step, and the tree is the one that scales
 * past a handful of each thing.
 */
function TopBar() {
  return (
    <header className="topbar">
      <a className="topbar__brand" href="/" aria-label="BroadPaper Cloud home">
        <img src="/logo.svg" alt="" />
      </a>
      <div className="grow" />
      <div className="topbar__right">
        {/*
          The way back to the website. The logo stays the application's own home,
          which is what it means inside a product; this is the one link out. Same tab,
          because it is leaving rather than looking something up.
        */}
        <a className="btn btn--quiet btn--sm" href={siteUrl("/")} title="The BroadPaper website">
          <Icon name="external" /> <span className="topbar__site-label">broadpaper.com</span>
        </a>
        <a className="btn btn--quiet btn--sm" href={helpUrl("/support/#common")} target="_blank" rel="noreferrer" title="Help and support, in a new tab">
          <Icon name="help" /> Help
        </a>
        <WorkspaceSwitcher />
        <Button
          size="sm"
          variant="quiet"
          onClick={() => {
            void clerk()?.signOut();
          }}
        >
          Sign out
        </Button>
      </div>
    </header>
  );
}

/**
 * Which workspace, as Clerk's own switcher.
 *
 * A colleague invited to somebody's workspace keeps their own, so one sign-in can
 * belong to several and needs a way between them — and the switcher is also where
 * an invitation waits for somebody who already has an account. Choosing one is a
 * full page load on purpose: every list on screen belongs to the workspace being
 * left. "Manage" goes to People rather than opening Clerk's modal, so there is one
 * place people are managed, and it is the one that settles the organisation first.
 * The personal account is hidden because a workspace always belongs to a business.
 */
function WorkspaceSwitcher() {
  const mount = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const instance = clerk();
    const element = mount.current;
    if (!instance || !element) return;
    instance.mountOrganizationSwitcher(element, {
      hidePersonal: true,
      afterSelectOrganizationUrl: "/",
      afterCreateOrganizationUrl: "/",
      createOrganizationMode: "modal",
      // Clerk's invitation step would send its invitations without an address to
      // land on; People's form is the way to invite, once the workspace exists.
      skipInvitationScreen: true,
      organizationProfileMode: "navigation",
      organizationProfileUrl: "/people"
    });
    return () => {
      try {
        instance.unmountOrganizationSwitcher(element);
      } catch {
        // Already torn down by Clerk, which throws on a second unmount.
      }
    };
  }, []);
  return <div ref={mount} className="topbar__switcher" />;
}

/* -------------------------------------------------------------- the screens */

function Screen({ path }: { path: string }) {
  // The designer is matched before the shell is rendered at all, so it is not here.
  const inner = (() => {
    if (matchRoute(path, "/")) return <Home />;
    if (matchRoute(path, "/data")) return <DataList folder={query(path).get("folder")} />;
    if (matchRoute(path, "/data/new")) return <DataImport />;
    // Before `/data/:id/edit`, which has the same shape.
    if (matchRoute(path, "/data/new/table")) return <DataEdit key="new" id={null} designId={query(path).get("design")} />;
    const editing = matchRoute(path, "/data/:id/edit");
    if (editing) return <DataEdit key={editing.id} id={editing.id!} designId={query(path).get("design")} />;
    const review = matchRoute(path, "/data/:id");
    if (review) return <DataReview id={review.id!} />;
    if (matchRoute(path, "/designs")) return <Designs preselectedDataset={query(path).get("data")} folder={query(path).get("folder")} starter={query(path).get("starter")} />;
    if (matchRoute(path, "/designs/import")) return <ImportDocument />;
    const history = matchRoute(path, "/designs/:id/history");
    if (history) return <History key={history.id} templateId={history.id!} selected={Number(query(path).get("v")) || null} />;
    const match = matchRoute(path, "/designs/:id/match");
    if (match) return <Matching templateId={match.id!} />;
    const fill = matchRoute(path, "/designs/:id/fill");
    if (fill) return <FillIn templateId={fill.id!} />;
    if (matchRoute(path, "/documents")) return <Documents folder={query(path).get("folder")} />;
    if (matchRoute(path, "/brand")) return <Brand />;
    if (matchRoute(path, "/account")) return <Account justPaid={query(path).get("checkout") === "done"} wanted={wantedPlan(path)} />;
    if (matchRoute(path, "/people")) return <People />;
    return (
      <Empty title="That page doesn't exist" actions={<a className="btn btn--primary" href="/">Go to your workspace</a>}>
        The address may have changed, or something may have been deleted.
      </Empty>
    );
  })();

  return <div className="main">{inner}</div>;
}

/**
 * A plan named in the address, as the pricing page sends it.
 *
 * The cadence is kept as the page said it, or null when it said nothing — never
 * defaulted to monthly here. Only a stated choice opens checkout by itself; an
 * address with no cadence lands on the plan screen, where both prices are shown.
 */
function wantedPlan(path: string): WantedPlan | null {
  const q = query(path);
  const plan = q.get("plan");
  if (!plan || !/^[a-z0-9-]{1,40}$/.test(plan)) return null;
  const cadence = q.get("cadence");
  return { plan, cadence: cadence === "annual" || cadence === "monthly" ? cadence : null };
}

/** Exposed so a screen can report a failure the same way everywhere. */
export function reportFailure(e: unknown, fallback: string): void {
  // A limit and a rate are not failures of anything: one is the plan and the other
  // is "wait a moment", and neither deserves the red that says something broke.
  if (e instanceof ApiError) toast(e.hint ? `${e.message} ${e.hint}` : e.message, e.code === "limit-reached" || e.code === "rate-limited" ? "info" : "bad");
  else toast(fallback, "bad");
}
